import { useCallback, useMemo, useState } from 'react';
import { DrawerForm, DrawerPanel } from '@dmc/ui';
import { VdsButton } from '@volt/volt-extended-components-react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@casops/store/hooks';
import Field from '@cas/components/inputs/field/Field';
import Dropdown from '@cas/components/inputs/dropdown/Dropdown';
import { DropdownItemProps } from '@cas/components/inputs/dropdown-items/DropdownItems';
import {
  AppearanceSetting,
  isAppearanceSetting,
  setAppearance,
} from '@casops/store/slices/prefsSlice';
import { closeSidePanel } from '@casops/store/slices/globalSlice';
import { isLanguageSetting, supportedLanguages } from '@casops/i18n';

interface DraftSettings {
  appearance: AppearanceSetting;
  language: string;
}

export function UserSettingsPanel() {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const appearanceRaw = useAppSelector(state => state.prefs.appearanceRaw);

  const [draft, setDraft] = useState<DraftSettings>({
    appearance: appearanceRaw,
    language: i18n.language ?? i18n.resolvedLanguage,
  });

  const appearanceItems = useMemo<DropdownItemProps<AppearanceSetting>[]>(() => {
    return [
      {
        key: AppearanceSetting.System,
        value: AppearanceSetting.System,
        stringValue: AppearanceSetting.System,
        label: t('appearance.Auto'),
      },
      {
        key: AppearanceSetting.Light,
        value: AppearanceSetting.Light,
        stringValue: AppearanceSetting.Light,
        label: t('appearance.Light'),
      },
      {
        key: AppearanceSetting.Dark,
        value: AppearanceSetting.Dark,
        stringValue: AppearanceSetting.Dark,
        label: t('appearance.Dark'),
      },
    ];
  }, [t]);

  const languageItems = useMemo<DropdownItemProps<string>[]>(() => {
    return supportedLanguages
      .map(lang => {
        const locale = new Intl.DisplayNames(lang, {
          type: 'language',
          languageDisplay: 'standard',
        });

        const languageInThatLocale = locale.of(lang);

        return { key: lang, value: lang, stringValue: lang, label: languageInThatLocale ?? lang };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  const onChangeAppearance = useCallback(
    (value: AppearanceSetting) => {
      if (isAppearanceSetting(value)) {
        setDraft({ ...draft, appearance: value });
      }
    },
    [draft],
  );

  const onChangeLanguage = useCallback(
    (value: string) => {
      if (isLanguageSetting(value)) {
        setDraft({ ...draft, language: value });
      }
    },
    [draft],
  );

  const onApplyChanges = useCallback(() => {
    if (draft.appearance !== appearanceRaw) {
      dispatch(setAppearance(draft.appearance));
    }

    if (draft.language !== i18n.language) {
      i18n.changeLanguage(draft.language);
    }

    dispatch(closeSidePanel());
  }, [appearanceRaw, dispatch, draft.appearance, draft.language, i18n]);

  const dirty = draft.appearance !== appearanceRaw || draft.language !== i18n.language;

  return (
    <DrawerPanel
      header={t('Settings')}
      footer={
        <VdsButton
          variant="primary"
          size="medium"
          disabled={!dirty}
          isFullWidth
          onClick={onApplyChanges}
        >
          {t('ApplyChange')}
        </VdsButton>
      }
    >
      <DrawerForm>
        <>
          <Field uid="appearance" label={t('Appearance')}>
            <Dropdown<AppearanceSetting>
              items={appearanceItems}
              onChange={onChangeAppearance}
              uid="appearance"
              value={draft.appearance}
            />
          </Field>

          {languageItems.length > 1 && (
            <Field uid="language" label={t('Language')}>
              <Dropdown<string>
                items={languageItems}
                onChange={onChangeLanguage}
                uid="language"
                value={draft.language}
                disabled={languageItems.length === 1}
              />
            </Field>
          )}
        </>
      </DrawerForm>
    </DrawerPanel>
  );
}

export default UserSettingsPanel;
